import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  Add,
  PhoneAndroidRounded,
  AccountBalance,
  ChevronRight,
  Lock,
} from "@mui/icons-material";
import { RootState } from "../../../../store/Index";
import { useSelector } from "react-redux";
import apiService from "../../../../service/Api";
import { PayoutAccount } from "../RequestPayouts";

interface SelectAccountProps {
  open: boolean;
  onClose: () => void;
  accounts: PayoutAccount[];
  onSelect: (account: PayoutAccount) => void;
  onAddNew: () => void;
}

interface PayoutAccountFull {
  id: number;
  method: string;
  ecocash_number?: string;
  bank_name?: string;
  account_name?: string;
  account_number?: string;
  is_default: boolean;
}

const MOBILE_METHODS = ["ECOCASH", "INNBUCKS", "OMARI", "ONEMONEY"];
const METHOD_LABELS: Record<string, string> = {
  ECOCASH: "Ecocash",
  INNBUCKS: "InnBucks",
  OMARI: "Omari",
  ONEMONEY: "OneMoney",
};

export default function SelectAccount({
  open,
  onClose,
  accounts,
  onSelect,
  onAddNew,
}: SelectAccountProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color400 = reduxTheme.primary_colors?.[400] ?? "#724E41";
  const color800 = reduxTheme.primary_colors?.[800] ?? "#f5ede9";

  const [fullDetails, setFullDetails] = useState<
    Record<number, PayoutAccountFull>
  >({});
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    if (!open || accounts.length === 0) return;

    const fetchAllFullDetails = async () => {
      setLoadingDetails(true);
      try {
        const results = await Promise.all(
          accounts.map((acct) =>
            apiService
              .get<PayoutAccountFull>(`/api/wallet/payout-accounts/${acct.id}/`)
              .then((res) => res.data)
              .catch(() => null),
          ),
        );
        const map: Record<number, PayoutAccountFull> = {};
        results.forEach((full) => {
          if (full) map[full.id] = full;
        });
        setFullDetails(map);
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchAllFullDetails();
  }, [open, accounts]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          m: 2,
          maxWidth: 360,
          width: "100%",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            pt: 2.5,
            pb: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "0.5px solid",
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 500, fontSize: "17px", color: "text.primary" }}
            >
              Pay to
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "12px" }}
            >
              {accounts.length} saved account{accounts.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: "text.secondary" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* Scrollable account list */}
        <Box
          sx={{
            maxHeight: 300,
            overflowY: "auto",
            px: 1.5,
            pt: 1.25,
            pb: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            maskImage:
              "linear-gradient(to bottom, black 85%, transparent 100%)",
          }}
        >
          {loadingDetails ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            accounts.map((acct) => {
              const isMobile = MOBILE_METHODS.includes(acct.method);
              const full = fullDetails[acct.id];
              const number = isMobile
                ? (full?.ecocash_number ?? acct.masked_ecocash_number ?? "")
                : (full?.account_number ?? acct.masked_account_number ?? "");
              const label = isMobile
                ? `${METHOD_LABELS[acct.method]} — ${number}`
                : `${acct.bank_name ?? "Bank"} — ${number}`;
              const sublabel = isMobile ? "Mobile wallet" : "Bank transfer";

              return (
                <Box
                  key={acct.id}
                  onClick={() => onSelect(acct)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: "12px",
                    bgcolor: "action.hover",
                    border: "0.5px solid",
                    borderColor: "divider",
                    cursor: "pointer",
                    flexShrink: 0,
                    transition: "all 0.15s",
                    "&:hover": {
                      borderColor: color400,
                      bgcolor: color800,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      bgcolor: acct.is_default
                        ? `${color400}18`
                        : "action.selected",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {isMobile ? (
                      <PhoneAndroidRounded
                        sx={{
                          fontSize: 16,
                          color: acct.is_default ? color400 : "text.secondary",
                        }}
                      />
                    ) : (
                      <AccountBalance
                        sx={{
                          fontSize: 16,
                          color: acct.is_default ? color400 : "text.secondary",
                        }}
                      />
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: "13px",
                        color: "text.primary",
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "11px", color: "text.secondary" }}
                    >
                      {sublabel}
                      {acct.is_default && (
                        <Box
                          component="span"
                          sx={{ ml: 0.5, color: color400, fontWeight: 600 }}
                        >
                          · Default
                        </Box>
                      )}
                    </Typography>
                  </Box>

                  <ChevronRight
                    sx={{ fontSize: 16, color: "text.disabled", flexShrink: 0 }}
                  />
                </Box>
              );
            })
          )}
        </Box>

        {/* Divider */}
        <Box sx={{ height: "0.5px", bgcolor: "divider", mx: 0 }} />

        {/* Add new — always visible, pinned outside scroll */}
        <Box sx={{ px: 1.5, pt: 1.25, pb: 1 }}>
          <Box
            onClick={onAddNew}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              borderRadius: "12px",
              border: `1px dashed`,
              borderColor: "divider",
              cursor: "pointer",
              bgcolor: "transparent",
              transition: "all 0.15s",
              "&:hover": {
                borderColor: color400,
                bgcolor: color800,
              },
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: "action.hover",
                border: "0.5px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Add sx={{ fontSize: 16, color: "text.secondary" }} />
            </Box>
            <Typography
              variant="body2"
              sx={{ fontSize: "13px", color: "text.secondary" }}
            >
              Add new account
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 2,
            pb: 2,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          <Lock sx={{ fontSize: 12, color: "text.disabled" }} />
          <Typography
            variant="caption"
            sx={{ fontSize: "11px", color: "text.disabled" }}
          >
            Tap an account to continue
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
